// Footer.jsx
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Footer.css";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const quickLinks = [
    { name: "Home", link: "/", id: "home" },
    { name: "About", link: "/#about", id: "about" },
    { name: "Services", link: "/#services", id: "services" },
    { name: "Contact", link: "/#contact", id: "contact" }
  ];

  const services = [
    "Wedding Decoration",
    "Home Decoration",
    "Birthday Decoration",
    "Corporate Events",
    "Baby Shower",
    "Anniversary Decoration"
  ];

  const socialLinks = [
    { icon: "fab fa-facebook-f", link: "#", name: "Facebook" },
    { icon: "fab fa-instagram", link: "#", name: "Instagram" },
    { icon: "fab fa-youtube", link: "#", name: "YouTube" },
    { icon: "fab fa-whatsapp", link: "https://wa.me/917023332099", name: "WhatsApp" }
  ];

  // Handle navigation with smooth scroll
  const handleNavigation = (e, targetLink) => {
    e.preventDefault();
    
    // If it's a hash link (starts with #)
    if (targetLink.startsWith('/#')) {
      const sectionId = targetLink.substring(1); // Remove the leading slash
      
      // If we're on the home page, just scroll
      if (location.pathname === "/") {
        const element = document.querySelector(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // Navigate to home with hash
        navigate(targetLink);
        // Scroll after navigation
        setTimeout(() => {
          const element = document.querySelector(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      }
    } else {
      // Regular link - navigate directly and scroll to top
      navigate(targetLink);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  // Handle home navigation (scroll to top)
  const handleHomeClick = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  // Handle Privacy Policy and Terms navigation
  const handlePolicyNavigation = (e, path) => {
    e.preventDefault();
    navigate(path);
    // Scroll to top immediately
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="footer" id="footer">
      <div className="footer-container">
        {/* Main Footer Content */}
        <div className="footer-content">
          {/* Column 1 - Brand */}
          <div className="footer-column">
            <a href="/" onClick={handleHomeClick} className="footer-brand">
              <i className="fas fa-magic"></i>
              <span>Atoz Events</span>
            </a>
            <p className="footer-description">
              We transform ordinary spaces into extraordinary memories with 
              our creative decoration services in Delhi and Gurugram.
            </p>
            <div className="footer-phone">
              <i className="fas fa-phone-alt"></i>
              <a href="tel:7023332099">7023332099</a>
            </div>
            <div className="footer-email">
              <i className="fas fa-envelope"></i>
              <a href="mailto:info@atozevents.in">info@atozevents.in</a>
            </div>
          </div>

          {/* Column 2 - Quick Links */}
          <div className="footer-column">
            <h4 className="footer-heading">Quick Links</h4>
            <ul className="footer-links">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.link} 
                    onClick={(e) => handleNavigation(e, link.link)}
                  >
                    <i className="fas fa-chevron-right"></i> {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Services */}
          <div className="footer-column">
            <h4 className="footer-heading">Our Services</h4>
            <ul className="footer-links">
              {services.map((service, index) => (
                <li key={index}>
                  <a 
                    href="/#services" 
                    onClick={(e) => handleNavigation(e, '/#services')}
                  >
                    <i className="fas fa-chevron-right"></i> {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Contact & Social */}
          <div className="footer-column">
            <h4 className="footer-heading">Get in Touch</h4>
            <div className="footer-address">
              <i className="fas fa-map-marker-alt"></i>
              <div>
                720/5, Patel Nagar,<br />
                Gurugram, Haryana 122001
              </div>
            </div>
            <div className="footer-hours">
              <i className="fas fa-clock"></i>
              <div>
                <span>Open 24/7</span>
                <span>Monday - Sunday</span>
              </div>
            </div>

            <div className="footer-social">
              <h5>Follow Us</h5>
              <div className="social-icons">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label={social.name}
                  >
                    <i className={social.icon}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="footer-copyright">
              &copy; {currentYear} <span>Atoz Events</span>. All Rights Reserved.
            </p>
            <div className="footer-bottom-links">
              <a 
                href="/privacy-policy" 
                onClick={(e) => handlePolicyNavigation(e, '/privacy-policy')}
              >
                Privacy Policy
              </a>
              <span className="footer-bottom-divider">|</span>
              <a 
                href="/terms" 
                onClick={(e) => handlePolicyNavigation(e, '/terms')}
              >
                Terms &amp; Conditions
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
     
    </footer>
  );
};

export default Footer;